import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, useTheme } from '../tokens';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

// USD accounts that need FX revaluation
const USD_ACCOUNTS = [
  { no: '11030', label: 'RBC - USD Chequing - Operating' },
  { no: '11040', label: 'RBC - USD Savings' },
  { no: '11060', label: 'RBC - USD Chequing - Payroll' },
  { no: '11080', label: 'Float - USD Chequing' },
  { no: '11110', label: 'Desjardins - USD Savings (EOP)' },
  { no: '11200', label: 'Desjardins - USD GIC (ET1-ET7)' },
  { no: '11220', label: 'VM Desjardins - USD Savings' },
  { no: '11240', label: 'RBC - USD GIC' },
];

function fmt(n) {
  if (n == null || isNaN(n)) return '--';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(n);
}

function fmtUSD(n) {
  if (n == null || isNaN(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

export default function FXCalcPanel({ month }) {
  const t = useTheme();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [jePosted, setJePosted] = useState(false);
  const [postingJe, setPostingJe] = useState(false);

  // Parse month for display
  const [year, mon] = month.split('-');
  const monthEnd = new Date(parseInt(year), parseInt(mon), 0); // last day of month
  const monthLabel = monthEnd.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  const handleRun = () => {
    setRunning(true);
    // TODO: Call backend which fetches BoC rate + GL balances from Sage
    setTimeout(() => {
      setResult(getDemoFXResult(month));
      setRunning(false);
    }, 2000);
  };

  const handlePostJE = () => {
    setPostingJe(true);
    // TODO: Call POST /api/sage/je/create
    setTimeout(() => {
      setJePosted(true);
      setPostingJe(false);
    }, 1500);
  };

  const totalFXAdjustment = result?.accounts.reduce((sum, a) => sum + a.fxAdjustment, 0) || 0;

  return (
    <div>
      {/* Header info */}
      <div style={{
        padding: '10px 12px', borderRadius: 8, marginBottom: 12,
        background: t.bgSurface || '#F8FAFC', border: `1px solid ${t.border}`,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 4 }}>
          FX Gain / Loss Calculator
        </div>
        <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans }}>
          Revalue all USD balance sheet accounts using BoC end-of-month exchange rate.
          Auto-generates reversing JE for Sage Intacct import.
        </div>
      </div>

      {!result && !running && (
        <div>
          {/* Info about what will happen */}
          <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: sans, lineHeight: 1.6, marginBottom: 14 }}>
            This will:
          </div>
          <div style={{ marginBottom: 14 }}>
            {[
              'Fetch BoC USD/CAD closing rate for ' + monthLabel,
              'Pull USD balances from bank statements',
              'Pull GL CAD carrying values from Sage',
              'Calculate FX adjustment per account',
              'Generate Sage import template (auto-reversing JE)',
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 11 }}>
                <span style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#F1F5F9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 700, color: '#64748B', fontFamily: mono, flexShrink: 0,
                }}>{i + 1}</span>
                <span style={{ color: t.textSecondary, fontFamily: sans }}>{step}</span>
              </div>
            ))}
          </div>

          <button
            onClick={handleRun}
            style={{
              width: '100%', padding: '12px', borderRadius: 8,
              border: 'none', cursor: 'pointer',
              background: green, color: '#fff',
              fontSize: 13, fontWeight: 700, fontFamily: sans,
              transition: 'all 0.2s',
            }}
          >
            RUN FX CALCULATION
          </button>
        </div>
      )}

      {running && (
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>
            Calculating FX revaluation...
          </div>
          <div style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.8 }}>
            Fetching BoC EOM rate...<br/>
            Pulling USD balances...<br/>
            Computing adjustments...
          </div>
        </div>
      )}

      {result && (
        <div>
          {/* Exchange Rate */}
          <div style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12,
            background: greenLight + '30', border: `1px solid ${green}40`,
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
              Exchange Rate
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: mono, color: greenDark }}>
                  {result.rate.toFixed(4)}
                </div>
                <div style={{ fontSize: 10, color: t.textFaint }}>CAD/USD</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontFamily: mono, color: t.textFaint }}>
                  Prior: {result.priorRate.toFixed(4)}
                </div>
                <div style={{ fontSize: 10, fontFamily: mono, color: result.rateChange < 0 ? greenDark : redDark }}>
                  Change: {(result.rateChange * 100).toFixed(2)}%
                </div>
                <div style={{ fontSize: 9, color: t.textFaint }}>Source: BoC Valet API</div>
              </div>
            </div>
          </div>

          {/* USD Account Revaluation Table */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              USD Account Revaluation
            </div>

            {/* Header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 75px 75px 75px',
              gap: 4, padding: '4px 6px', borderBottom: `1px solid ${t.border}`,
              fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase',
            }}>
              <span>Account</span>
              <span style={{ textAlign: 'right' }}>USD Bal</span>
              <span style={{ textAlign: 'right' }}>GL CAD</span>
              <span style={{ textAlign: 'right' }}>FX Adj</span>
            </div>

            {result.accounts.map(acct => (
              <div key={acct.no} style={{
                display: 'grid', gridTemplateColumns: '1fr 75px 75px 75px',
                gap: 4, padding: '5px 6px', borderBottom: `1px solid ${t.border}`,
                fontSize: 10, fontFamily: mono,
                opacity: acct.usdBalance === 0 ? 0.4 : 1,
              }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: t.textPrimary, fontFamily: sans }}>{acct.label}</div>
                  <div style={{ fontSize: 9, color: t.textFaint }}>{acct.no}</div>
                </div>
                <span style={{ textAlign: 'right', color: t.textSecondary }}>{fmtUSD(acct.usdBalance)}</span>
                <span style={{ textAlign: 'right', color: t.textSecondary }}>{fmt(acct.glCadValue)}</span>
                <span style={{
                  textAlign: 'right', fontWeight: 600,
                  color: acct.fxAdjustment < 0 ? red : acct.fxAdjustment > 0 ? greenDark : t.textFaint,
                }}>
                  {acct.fxAdjustment !== 0 ? fmt(acct.fxAdjustment) : '--'}
                </span>
              </div>
            ))}

            {/* Total */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 75px 75px 75px',
              gap: 4, padding: '8px 6px',
              fontSize: 11, fontFamily: mono, fontWeight: 700,
              borderTop: `2px solid ${t.border}`,
            }}>
              <span style={{ color: t.textPrimary }}>TOTAL FX {totalFXAdjustment < 0 ? 'LOSS' : 'GAIN'}</span>
              <span />
              <span />
              <span style={{
                textAlign: 'right',
                color: totalFXAdjustment < 0 ? red : greenDark,
              }}>
                {fmt(totalFXAdjustment)}
              </span>
            </div>
          </div>

          {/* Proposed JE */}
          <div style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12,
            background: tealLight + '15', border: `1px solid ${teal}30`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: teal, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Journal Entry (auto-reversing)
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '60px 1fr 70px 70px',
              gap: 4, padding: '4px 0', borderBottom: `1px solid ${t.border}`, marginBottom: 4,
              fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase',
            }}>
              <span>Account</span>
              <span>Description</span>
              <span style={{ textAlign: 'right' }}>Debit</span>
              <span style={{ textAlign: 'right' }}>Credit</span>
            </div>

            {result.accounts.filter(a => a.fxAdjustment !== 0).map(acct => (
              <div key={acct.no} style={{
                display: 'grid', gridTemplateColumns: '60px 1fr 70px 70px',
                gap: 4, padding: '3px 0', fontSize: 10, fontFamily: mono,
              }}>
                <span style={{ color: teal, fontWeight: 600 }}>{acct.no}</span>
                <span style={{ color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  FX reval {acct.label.split(' - ')[1] || acct.label}
                </span>
                <span style={{ textAlign: 'right' }}>
                  {acct.fxAdjustment > 0 ? fmt(acct.fxAdjustment) : '--'}
                </span>
                <span style={{ textAlign: 'right' }}>
                  {acct.fxAdjustment < 0 ? fmt(Math.abs(acct.fxAdjustment)) : '--'}
                </span>
              </div>
            ))}

            {/* Offset line (70200) */}
            <div style={{
              display: 'grid', gridTemplateColumns: '60px 1fr 70px 70px',
              gap: 4, padding: '3px 0', fontSize: 10, fontFamily: mono,
              borderTop: `1px solid ${t.border}`, marginTop: 2, paddingTop: 4,
              fontWeight: 600,
            }}>
              <span style={{ color: teal }}>70200</span>
              <span style={{ color: t.textPrimary }}>FX Gain/Loss</span>
              <span style={{ textAlign: 'right' }}>
                {totalFXAdjustment < 0 ? fmt(Math.abs(totalFXAdjustment)) : '--'}
              </span>
              <span style={{ textAlign: 'right' }}>
                {totalFXAdjustment > 0 ? fmt(totalFXAdjustment) : '--'}
              </span>
            </div>

            <div style={{ fontSize: 9, color: t.textFaint, marginTop: 6, fontFamily: sans }}>
              Date: {month}-{new Date(parseInt(year), parseInt(mon), 0).getDate()} | Reversal: {month === '2026-12' ? '2027-01-01' : year + '-' + String(parseInt(mon) + 1).padStart(2, '0') + '-01'} | Journal: FX
            </div>

            {/* Post to Sage button */}
            <button
              onClick={handlePostJE}
              disabled={jePosted || postingJe}
              style={{
                width: '100%', padding: '10px', borderRadius: 6, marginTop: 8,
                border: 'none',
                background: jePosted ? greenLight : postingJe ? '#E2E8F0' : teal,
                color: jePosted ? greenDark : postingJe ? '#94A3B8' : '#fff',
                fontSize: 12, fontWeight: 700, cursor: jePosted ? 'default' : 'pointer',
                fontFamily: sans, transition: 'all 0.2s',
              }}
            >
              {jePosted ? 'Posted to Sage \u2713' : postingJe ? 'Posting...' : 'Post FX JE to Sage'}
            </button>
          </div>

          {/* Download template */}
          <button style={{
            width: '100%', padding: '10px', borderRadius: 6,
            border: `1px solid ${green}`, background: greenLight,
            color: greenDark, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
            marginBottom: 8,
          }}>
            Download FX Calculator Template
          </button>

          <button onClick={() => { setResult(null); setJePosted(false); }} style={{
            width: '100%', padding: '8px', borderRadius: 6,
            border: `1px solid ${t.border}`, background: 'transparent',
            color: t.textMuted, fontSize: 10, cursor: 'pointer', fontFamily: sans,
          }}>
            Reset &amp; Recalculate
          </button>
        </div>
      )}
    </div>
  );
}

// Demo FX result
function getDemoFXResult(month) {
  const rate = 1.4385; // BoC Feb 28, 2026 rate (demo)
  const priorRate = 1.3619; // Jan 31 rate

  const accounts = USD_ACCOUNTS.map(a => {
    // Demo USD balances and GL CAD values
    const demos = {
      '11030': { usd: 212543.18, glCad: 296785.50 },
      '11040': { usd: 0, glCad: 0 },
      '11060': { usd: 0, glCad: 0 },
      '11080': { usd: 108234.56, glCad: 155591.38 },
      '11110': { usd: 49.89, glCad: 67.95 },
      '11200': { usd: 11307180.82, glCad: 15399249.56 },
      '11220': { usd: 610493.29, glCad: 831430.81 },
      '11240': { usd: 15000000.00, glCad: 20428500.00 },
    };
    const d = demos[a.no] || { usd: 0, glCad: 0 };
    const newCadValue = d.usd * rate;
    const fxAdj = newCadValue - d.glCad;

    return {
      ...a,
      usdBalance: d.usd,
      glCadValue: d.glCad,
      newCadValue: Math.round(newCadValue * 100) / 100,
      fxAdjustment: Math.round(fxAdj * 100) / 100,
    };
  });

  return {
    rate,
    priorRate,
    rateChange: (rate - priorRate) / priorRate,
    accounts,
  };
}
